import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { navLinks } from '../data/navigation';
import { useLanguage } from '../contexts/LanguageContext';
import type { Language } from '../types/language';
import { Button } from './Button';
function LanguageSwitch({
  className = ''
}: {
  className?: string;
}) {
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  return <div role="group" aria-label={t.navigation.language} className={`flex items-center border border-ivory/25 p-0.5 ${className}`} data-id="element-82" data-mp="a7iuogejuznt">
      {(['en', 'es'] as Language[]).map(option => <button key={option} type="button" onClick={() => setLanguage(option)} aria-pressed={language === option} className={`min-h-9 min-w-11 px-2 text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gold ${language === option ? 'bg-ivory text-ink' : 'text-ivory/55 hover:text-ivory'}`} data-id="element-83" data-mp="k1rkgwc74eia">
          {option}
        </button>)}
    </div>;
}
export function Navigation() {
  const {
    t
  } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return <>
      <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-200 ${isScrolled || isOpen ? 'border-b border-ivory/10 bg-ink/95 backdrop-blur-md' : 'bg-transparent'}`} data-id="element-84" data-mp="157568246798z">
        <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-5 md:px-8 lg:px-12" data-id="element-85" data-mp="1f51kv3wbgmbu">
          <a href="#top" className="relative z-50 text-sm font-semibold tracking-[0.18em] text-ivory focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-86" data-mp="1k3w8cj58az2a">
            LEONARDO TOVAR
          </a>

          <nav aria-label={t.navigation.aria} className="hidden items-center gap-7 lg:flex" data-id="element-87" data-mp="ph5trpjkr51n">
            {navLinks.map(link => <a key={link.href} href={link.href} className="whitespace-nowrap text-xs font-medium text-ivory/75 transition-colors duration-200 hover:text-ivory focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-88" data-mp="146v7slcrn3jc">
                {t.navigation[link.key]}
              </a>)}
          </nav>

          <div className="hidden items-center gap-3 lg:flex" data-id="element-89" data-mp="186dlox666eco">
            <LanguageSwitch data-id="element-90" data-mp="1n4eaz8cjlrxu" />
            <Button href="#quote" className="px-6 py-3.5" data-id="element-91" data-mp="1n3moqo4u56k8">{t.navigation.cta}</Button>
          </div>

          <div className="relative z-50 flex items-center gap-2 lg:hidden" data-id="element-92" data-mp="1i6mlekzdehwt">
            <LanguageSwitch data-id="element-93" data-mp="3oviyptbcu8cw" />
            <button type="button" aria-label={isOpen ? t.navigation.close : t.navigation.open} aria-expanded={isOpen} onClick={() => setIsOpen(current => !current)} className="flex h-11 w-11 items-center justify-center text-ivory focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-94" data-mp="3owu4tzs9vs76">
              {isOpen ? <XIcon size={24} data-id="element-95" data-mp="1ppvsu8obg6j8" /> : <MenuIcon size={24} data-id="element-96" data-mp="1poax4fq9mhjs" />}
            </button>
          </div>
        </div>

        <AnimatePresence data-id="element-97" data-mp="1un5erechy02b">
          {isOpen ? <motion.div initial={{
          opacity: 0,
          y: -12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.22,
          ease: [0.22, 1, 0.36, 1]
        }} className="absolute inset-x-0 top-20 border-t border-ivory/10 bg-ink px-5 py-8 lg:hidden" data-id="element-98" data-mp="1ulualdf52qao">
              <nav aria-label={t.navigation.mobileAria} className="flex flex-col" data-id="element-99" data-mp="1bqdzrh3zm742">
                {navLinks.map(link => <a key={link.href} href={link.href} onClick={() => setIsOpen(false)} className="border-b border-ivory/10 py-4 font-display text-2xl text-ivory focus:outline-none focus:ring-2 focus:ring-gold" data-id="element-100" data-mp="3152aldwxjgzz">
                    {t.navigation[link.key]}
                  </a>)}
                <Button href="#quote" onClick={() => setIsOpen(false)} className="mt-8 w-full" data-id="element-101" data-mp="112jck88ifhdz">{t.navigation.cta}</Button>
              </nav>
            </motion.div> : null}
        </AnimatePresence>
      </header>

      <div className="fixed bottom-3 left-3 right-3 z-40 lg:hidden" data-id="element-102" data-mp="s3o1oiuitj4c">
        <Button href="#quote" className="w-full py-3.5" data-id="element-103" data-mp="n3taee6u2rir">{t.navigation.cta}</Button>
      </div>
    </>;
}